import { useState, useEffect } from 'react'
import { Formik, Form } from 'formik'
import * as Analytics from 'core/common/analytics'
import i18n from 'core/common/i18n'
import { getErrorMessage, validateField } from 'core/common/utils/validation'
import CardStep from 'core/components/CardStep'
import { Link, Button } from 'core/components/Core'
import TextField from 'core/components/TextField'
import { useFormContext } from 'core/pages/ProductNew-v2/context/ProductNewContext'
import { URL_ANATEL_PRODUCT_CERTIFICATION } from './constants'
import { AnatelCertificationFormSchema } from './schema'

export const AnatelCertificationForm = ({
  onNext,
  isEditMode,
  handleEditStep,
  step,
  formMode,
  onEditSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { formData, setFormData } = useFormContext()
  const initialValues = { anatel: formData.anatel }

  useEffect(() => {
    const submitEdit = async () => {
      if (isSubmitting && isEditMode && onEditSubmit) {
        setIsSubmitting(false)
        setIsLoading(true)
        try {
          await onEditSubmit(formData)
          setIsLoading(false)
        } catch (error) {
          console.error('Error during API call', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    submitEdit()
  }, [isSubmitting, formData, isEditMode, onEditSubmit])

  const onSubmit = values => {
    Analytics.ProductAnatelCertificationSaved(formMode)
    setFormData(prevData => ({ ...prevData, anatel: values.anatel }))

    setIsSubmitting(true)
    onNext()
  }

  const handleValidation = async ({ validateForm }) => {
    const errors = await validateForm()

    if (Object.keys(errors).length) {
      Analytics.ProductAnatelCertificationValidationError(formMode)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={AnatelCertificationFormSchema}
      enableReinitialize
    >
      {({
        handleSubmit,
        handleChange,
        values,
        errors,
        touched,
        submitCount,
        handleBlur,
        resetForm,
        validateForm,
        dirty,
      }) => {
        return (
          <Form className="w-100">
            <CardStep
              title={!isEditMode && i18n.t('productNew.anatelCertification.title')}
              description={
                <>
                  {i18n.t('productNew.anatelCertification.description')}{' '}
                  <Link
                    style={{ fontWeight: 500 }}
                    href={URL_ANATEL_PRODUCT_CERTIFICATION}
                    rel="noreferrer"
                    target="_blank"
                    onClick={() => Analytics.ProductAnatelCertificationLinkClicked(formMode)}
                  >
                    {i18n.t('productNew.anatelCertification.anatelLink')}
                  </Link>
                </>
              }
            >
              <TextField
                error={validateField('anatel', errors, touched, submitCount > 0)}
                onBlur={handleBlur}
                variant="outlined"
                helperText={getErrorMessage('anatel', errors, touched)}
                label={i18n.t('productNew.anatelCertification.homologationLabel')}
                value={values?.anatel}
                name="anatel"
                className="w-50"
                onChange={e => {
                  handleChange(e)
                  handleEditStep(step.key)
                }}
                fullWidth
                sanitize
                defaultValue={values?.anatel}
              />

              <div className="w-100 d-flex justify-end">
                {!isEditMode && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => {
                        resetForm()
                        handleEditStep(null)
                      }}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleSubmit()
                        handleValidation({ validateForm })
                      }}
                    >
                      {i18n.t('productNew.actions.continue')}
                    </Button>
                  </>
                )}

                {isEditMode && (dirty || isLoading) && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => resetForm()}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleSubmit()
                        handleValidation({ validateForm })
                      }}
                      loading={isLoading}
                    >
                      <span>Salvar</span>
                    </Button>
                  </>
                )}
              </div>
            </CardStep>
          </Form>
        )
      }}
    </Formik>
  )
}
